
import { useState,useRef } from "react";
import {
  Drawer,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown";
import { useTranslation } from "react-i18next";
import CustomButton from "../../../../components/CustomButton";
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { toast } from "react-hot-toast";
import { getApiMessage } from "../../../../utils/messageHandler";
import document1 from "../../../../assets/document1.svg";
import imageicon from "../../../../assets/imageicon.png";
import Webcam from "react-webcam";
import { Menu, MenuItem } from "@mui/material";
import { convertLocalDateTimeToUTC } from "../../../../components/dateTimeUtils";

const SMALL_LABEL_WORDS = new Set(["of", "and", "or", "the", "a", "an"]);

const FIELD_LABEL_KEYS = {
  date_of_expense: "expenses.labels.dateOfExpense",
  amount: "expenses.labels.amount",
  currency: "expenses.labels.currency",
  description: "expenses.labels.description",
  receipt_url: "expenses.labels.receipt",
};

const humanizeSnakeCaseField = (str) => {
  if (!str || typeof str !== "string") return str;
  const parts = str.split("_").filter(Boolean);
  if (parts.length === 0) return str;
  return parts
    .map((word, i) => {
      const lower = word.toLowerCase();
      if (lower === "url") return "URL";
      if (lower === "id") return "ID";
      if (i > 0 && SMALL_LABEL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(" ");
};

/** Translates a backend field name (e.g. "date_of_expense") using the same labels shown on the form. */
const translateField = (field, t) => {
  const key = FIELD_LABEL_KEYS[field];
  const translated = key ? t(key) : null;
  return translated && translated !== key ? translated : humanizeSnakeCaseField(field);
};

/** Turns backend lists like "date_of_expense, amount, receipt_url" into readable, localized labels for toasts. */
const humanizeApiFieldsListMessage = (message, t) => {
  if (!message || typeof message !== "string") return message;
  const colonIdx = message.indexOf(":");
  if (colonIdx === -1) return message;
  const prefix = message.slice(0, colonIdx + 1).trimEnd();
  const listPart = message.slice(colonIdx + 1).trim();
  const fields = listPart.split(",").map((s) => s.trim()).filter(Boolean);
  if (fields.length === 0) return message;
  const allFieldLike = fields.every((f) => /^[a-zA-Z][a-zA-Z0-9_]*$/.test(f));
  if (!allFieldLike) return message;
  return `${prefix} ${fields.map((f) => translateField(f, t)).join(", ")}`;
};

const AddExpenseDrawer = ({ open, onClose, refreshList }) => {
  const { t } = useTranslation();
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;
const initialFormState = {
  date_of_expense: "",
  amount: "",
  currency: "",
  description: "",
  receipt_url: "",
  proof: null,
};

  const [saving, setSaving] = useState(false);
const [formValues, setFormValues] = useState(initialFormState);

const [anchorEl, setAnchorEl] = useState(null);
const openMenu = Boolean(anchorEl);

  const startRef = useRef(null);

const [showCamera, setShowCamera] = useState(false);
const webcamRef = useRef(null);
const [capturedImage, setCapturedImage] = useState(null);
const [takingPhoto, setTakingPhoto] = useState(false);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = ({ target: { name, value } }) => {
    setFormValues((prev) => ({
      ...prev,
      [name]: value.length ? value[0] : "",
    }));
  };

const handleFileUpload = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  try {
    const formData = new FormData();

    const isPDF = file.type === "application/pdf";

    // Correct field key for backend
    const fieldKey = isPDF ? "pdf" : "image";
    formData.append(fieldKey, file);

    // Correct endpoint for backend
    const endpoint = isPDF ? "upload/pdf" : "upload/image";

    const res = await fetch(`${BASE_URL}/${endpoint}`, {
      method: "POST",
      body: formData,
    });

    const json = await res.json();

    if (!json.error && json.data?.url) {
      setFormValues((prev) => ({
        ...prev,
        proof: file,
        receipt_url: json.data.url,
      }));

toast.success(t("requests.uploadSuccess"));    } else {
      toast.error(json.message || "Upload failed");
    }
  } catch (err) {
toast.error(t("requests.uploadError"));    toast.error("Error uploading file");
  }
};


  const handleSave = async () => {
    try {
      setSaving(true);

      const payload = {
        date_of_expense: convertLocalDateTimeToUTC(formValues.date_of_expense),
        amount: parseFloat(formValues.amount),
        currency: formValues.currency,
        description: formValues.description,
        receipt_url: formValues.receipt_url,
      };

      console.log("➡️ Final payload:", payload);

      const res = await fetch(`${BASE_URL}/worker/expenses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok || json.error) {
        // Show backend message in appropriate language
        toast.error(
          humanizeApiFieldsListMessage(
            getApiMessage(json, t("expenses.messages.error")),
            t
          )
        );
      } else {
        toast.success(getApiMessage(json, t("expenses.messages.success")));
       setFormValues(initialFormState);
setCapturedImage(null);
setShowCamera(false);
setAnchorEl(null);
onClose();
refreshList?.();
      }
    } catch (err) {
      console.error("❌ Error saving expense:", err);
      toast.error(
        humanizeApiFieldsListMessage(
          getApiMessage({ message: err.message }, t("expenses.messages.apiError")),
          t
        )
      );
    } finally {
      setSaving(false);
    }
  };



  const handleDrawerClose = () => {
  setFormValues(initialFormState);
  setCapturedImage(null);
  setShowCamera(false);
  setAnchorEl(null);
  onClose();
};


  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleDrawerClose}
      PaperProps={{
        sx: { width: 650, maxWidth: "100%", display: "flex", flexDirection: "column" },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #E0E0E0",
          backgroundColor: "#f5f6f7",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={handleDrawerClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 18,
              fontWeight: 600,
              color: "#172B4D",
            }}
          >
            {t("expenses.addHeading") || "Add Expense"}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, flexGrow: 1, overflowY: "auto" }}>
        {/* Date */}
        <Box mb={2}>
          <label className="field_label"> {t("expenses.labels.dateOfExpense")} <span style={{ color: "red" }}>*</span></label>
          <input
             ref={startRef}
    onClick={() => startRef.current?.showPicker()}
            type="date"
            name="date_of_expense"
            value={formValues.date_of_expense}
            onChange={handleChange}
            className="project_input"
            style={{ height: 40, width: "100%", marginTop: 6 }}
          />
        </Box>

        {/* Amount + Currency (same row) */}
        <Box display="flex" gap={2} mb={2}>
          <Box sx={{ flex: 1 }}>
            <label className="field_label">{t("expenses.labels.amount")}  <span style={{ color: "red" }}>*</span></label>
            <input
              type="number"
              name="amount"
              value={formValues.amount}
              onChange={handleChange}
              className="project_input"
              style={{ height: 40, width: "100%", marginTop: 6 }}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <MultiSelectDropdown
              label={
                <>
                  {t("expenses.labels.currency") || "Currency"}{" "}
                  <span style={{ color: "red" }}>*</span>
                </>
              }
              name="currency"
              values={formValues.currency ? [formValues.currency] : []}
              onChange={handleDropdownChange}
              options={[
                { label: "USD", value: "USD" },
             
              ]}
            />
          </Box>
        </Box>

        {/* Description */}
        <Box mb={2}>
          <label className="field_label"> {t("expenses.labels.description")} </label>
          <textarea
            name="description"
            value={formValues.description}
            onChange={handleChange}
            rows={4}
            className="project_input"
            style={{ width: "100%", marginTop: 6, paddingTop: "5px" }}
          />
        </Box>

        {/* Proof Upload */}
        <CustomText
          sx={{
            fontSize: 16,
            fontWeight: 500,
            mb: 1,
            color: "#172B4D",
            textAlign: "left",
            fontFamily: "Poppins, sans-serif",
          }}
        >
 {t("expenses.labels.receipt")}   <span style={{ color: "red" }}>*</span>      </CustomText>

        <Box
          sx={{
            border: "2px dashed #D0D5DD",
            borderRadius: 2,
            p: 3,
            backgroundColor: "#F9FAFB",
            cursor: "pointer",
            mb: 3,
            height: 140,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
          onClick={(e) => {
e.stopPropagation();
setAnchorEl(e.currentTarget);
}}
        >
          {!formValues.proof ? (
            // <label htmlFor="proof-upload" style={{ cursor: "pointer" }}>
            <>
              <input
                type="file"
                id="proof-upload"
                accept="image/*,application/pdf"
                onChange={handleFileUpload}
                hidden
              />
              <CustomText sx={{ fontSize: 16, fontWeight: 500 ,        fontFamily: 'Poppins, sans-serif',
}}>
                 {t("expenses.upload.dragOrClick")}{" "}
        <span style={{ textDecoration: "underline", color: "#006EC2" }}>
          {t("expenses.upload.browse")}
        </span>
              </CustomText>
              </>
            // </label>
          ) : (
            <Box
              sx={{
                // width: "100%",
                // display: "flex",
                // alignItems: "center",
                // justifyContent: "center",
                // position: "relative",
                     width: "100%",
        textAlign: "center",
        wordBreak: "break-all",
        position: "relative",
              }}
            >
       <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>

 <img
    src={
      formValues.proof?.type === "application/pdf"
        ? document1     // PDF icon
        : imageicon     // Image icon
    }
    alt="file-icon"
    style={{
      width: 30,
      height:30,
      objectFit: "contain",
      marginBottom:2,
    }}
  />
</Box>
  {/* File Name Below Icon */}
  <CustomText
    sx={{
      fontSize: 14,
      fontWeight: 500,
      fontFamily: "Poppins, sans-serif",
      color: "#172B4D",
      textAlign: "center",
      maxWidth: "100%",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    }}
  >
    {formValues.proof.name}
  </CustomText>

              <IconButton
                size="small"
                sx={{ position: "absolute", top: 4, right: 4 }}
                onClick={() =>
                  setFormValues((prev) => ({ ...prev, proof: null, receipt_url: "" }))
                }
              >
                ✕
              </IconButton>
            </Box>
          )}
        </Box>
<Menu
  anchorEl={anchorEl}
  open={openMenu}
  onClose={() => setAnchorEl(null)}
>
  <MenuItem
    onClick={() => {
      setAnchorEl(null);
      setCapturedImage(null);
      setShowCamera(true);
    }}
        sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}

  >
    {t("takePhoto") || "Take Photo"}
  </MenuItem>

  <MenuItem
    onClick={() => {
      setAnchorEl(null);
      document.getElementById("proof-upload")?.click();
    }}
        sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}

  >
    {t("chooseFromGallery") || "Choose from Gallery"}
  </MenuItem>
</Menu>
        {/* Save Button */}
        <CustomButton
          label={
            saving ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={16} sx={{ color: "white" }} />
                {t("expenses.actions.save") || "Save"}
              </Box>
            ) : (
              t("expenses.actions.save") || "Save"
            )
          }
          sx={{ mt: 1 }}
          onClick={handleSave}
          size="small"
          disabled={saving}
        />
      </Box>


      {showCamera && (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1300,
      p: 2,
    }}
  >
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        p: 3,
        width: "100%",
        maxWidth: 450,
      }}
    >
      {!capturedImage ? (
        <Webcam
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: "environment" }}
          style={{ width: "100%", borderRadius: 8 }}
        />
      ) : (
        <img
          src={capturedImage}
          alt="Captured"
          style={{ width: "100%", borderRadius: 8 }}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
        {!capturedImage ? (
          <CustomButton
            label={takingPhoto ? "Uploading..." : t("capture") || "Capture"}
            disabled={takingPhoto}
            onClick={async () => {
              const img = webcamRef.current?.getScreenshot();
              if (!img) return;

              setTakingPhoto(true);
              setCapturedImage(img);

              const blob = await fetch(img).then((r) => r.blob());
              const file = new File([blob], "receipt.jpg", {
                type: "image/jpeg",
              });

              // 👇 reuse existing upload handler
              const fakeEvent = {
                target: { files: [file] },
              };
              await handleFileUpload(fakeEvent);

              setTakingPhoto(false);
              setShowCamera(false);
            }}
          />
        ) : (
          <CustomButton
            label={t("retake") || "Retake"}
            onClick={() => setCapturedImage(null)}
          />
        )}

        <CustomButton
          label={t("close") || "Close"}
          onClick={() => setShowCamera(false)}
        />
      </Box>
    </Box>
  </Box>
)}
    </Drawer>
  );
};

export default AddExpenseDrawer;

